import { i18n } from '../i18n.js';
import { Firebase } from '../firebase-loader.js';
import { MoonfallGuest } from '../guest-session.js';
import { MoonfallVoucherLink } from '../voucher-link.js';
import { CardTilt } from './card-tilt.js';
import {
  holdBootVeilForTicket,
  keepTicketStubFitted,
  releaseBootVeilForTicket,
  warmInviteTicketArt,
  whenInviteTicketWhole,
} from './invite-ticket-object.js';
// The voucher prints on its own ticket art — the gift-and-seal variant of the
// invitation's crescent-moon ticket, same printer, distinct face.
const VOUCHER_TICKET_ART_SRC = '/assets/images/voucher-ticket.webp';
import { SoundEffects } from './sound-effects.js';
import { VoucherRedeem } from './voucher-redeem.js';
import { voucherCodeHint } from './voucher-code.js';

/**
 * VoucherTicket — the arrival screen a voucher link raises.
 *
 * `/play?redeem=CODE` used to be nothing more than a query string. The link a
 * creator posts, or a Discord drop carries, now lands on the OBJECT: the same
 * ticket the invitation flow hands over, printed as a voucher — what it grants,
 * a serial, the issue month — with one thing to do: claim it.
 *
 * It is presentation plus one call. The grants it prints come from the server
 * (seeded into the page for a link the server confirmed, or fetched from
 * `/api/voucher/preview` after the address bar was stripped), and claiming is
 * `VoucherRedeem.redeemCode`, the same path the typed dialog uses. A visitor
 * without an account is sent to sign in; the code waits in this tab and the
 * ticket comes back in the lobby, queued behind the arrival ceremony.
 *
 * Contract: tests/unit/ui/voucherTicket.test.js
 */
let VoucherTicket = {};

VoucherTicket = (function () {
  const ROOT_ID = 'voucher-ticket-view';
  const PREVIEW_KEY = 'moonfall.pendingVoucherPreview';
  const PASS_TIERS = { moonfall_storyteller: 'store.storytellerPass', moonfall_manga: 'store.mangaPass' };
  const PASS_FALLBACKS = { moonfall_storyteller: 'Storyteller Pass', moonfall_manga: 'Manga Pass' };
  const PRESENT_CAP_MS = 6000;

  function uiText(key, fallback, replacements) {
    try {
      const value = i18n?.t?.(key, replacements);
      if (typeof value === 'string' && value && value !== key) return value;
    } catch (_) { /* i18n not ready */ }
    if (!replacements) return fallback;
    return Object.keys(replacements).reduce(
      (text, token) => text.split(`{{${token}}}`).join(String(replacements[token])),
      fallback,
    );
  }

  function pendingCode() {
    try { return MoonfallVoucherLink?.getPendingCode?.() || null; } catch (_) { return null; }
  }

  /* ---------------------------------------------------------- previews */

  function seededPreview() {
    const seed = document.querySelector('meta[name="moonfall:voucher"]');
    if (!seed) return null;
    // The server writes the code's state into the seed, so a revoked or spent
    // code's link opens a blocked ticket in its first frame, never a gift.
    const state = String(seed.getAttribute('content') || 'active');
    const raw = document.querySelector('meta[name="moonfall:voucher-grants"]')?.getAttribute('content');
    try {
      const grants = JSON.parse(raw || '[]');
      return { exists: true, state, grants: Array.isArray(grants) ? grants : [] };
    } catch (_) {
      return { exists: true, state, grants: [] };
    }
  }

  function storedPreview(code) {
    try {
      const raw = window.sessionStorage.getItem(PREVIEW_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && parsed.code === code && Array.isArray(parsed.grants) ? parsed : null;
    } catch (_) {
      return null;
    }
  }

  function storePreview(code, preview) {
    try {
      window.sessionStorage.setItem(PREVIEW_KEY, JSON.stringify({ code, state: preview.state, grants: preview.grants }));
    } catch (_) { /* a stash is a courtesy */ }
  }

  function clearStoredPreview() {
    try { window.sessionStorage.removeItem(PREVIEW_KEY); } catch (_) { /* ignore */ }
  }

  async function fetchPreview(code) {
    if (typeof fetch !== 'function') return null;
    try {
      const response = await fetch(`/api/voucher/preview?code=${encodeURIComponent(code)}`, {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (data?.exists !== true) return { exists: false, state: 'unknown', grants: [] };
      return { exists: true, state: String(data.state || 'active'), grants: Array.isArray(data.grants) ? data.grants : [] };
    } catch (_) {
      return null;
    }
  }

  /* ---------------------------------------------------------- the copy */

  function passName(tier) {
    return uiText(PASS_TIERS[tier], PASS_FALLBACKS[tier] || 'Moon Pass');
  }

  /* The reward as a SHORT stamp for the ticket face — a pass names itself, else
     a plain count — so a long grant line never overruns the stamp. Mirrors
     game/utils/voucherGrantSummary.js:voucherAdmitStamp. */
  function voucherAdmitStamp(grants) {
    const list = Array.isArray(grants) ? grants : [];
    const pass = list.find((grant) => grant && grant.kind === 'complimentary_pass');
    if (pass) return passName(pass.tier);
    return list.length <= 1
      ? uiText('voucher.ticketReward', 'ONE REWARD')
      : uiText('voucher.ticketRewards', '{{count}} rewards', { count: list.length });
  }

  function grantLine(grant) {
    if (!grant || typeof grant !== 'object') return null;
    switch (grant.kind) {
      case 'complimentary_pass':
        return Number(grant.boostUsdMicros) > 0
          ? uiText('voucher.grantPassBoost', '{{pass}} · four weeks of weekly Moonlight, with extra Moonlight', { pass: passName(grant.tier) })
          : uiText('voucher.grantPass', '{{pass}} · four weeks of weekly Moonlight', { pass: passName(grant.tier) });
      case 'stardust':
        return uiText('voucher.grantStardust', '+{{amount}} Stardust', { amount: Number(grant.amount || 0) });
      case 'card_packs':
        return Number(grant.count) === 1
          ? uiText('voucher.grantPackOne', '+1 card pack')
          : uiText('voucher.grantPacks', '+{{count}} card packs', { count: Number(grant.count || 0) });
      case 'moonlight':
        return uiText('voucher.grantMoonlightAmount', 'Extra Moonlight play time');
      case 'cardback':
        return uiText('voucher.grantCardback', 'A new cardback');
      default:
        return null;
    }
  }

  function stateCopy(state) {
    switch (state) {
      case 'expired': return uiText('voucher.error.expired', 'This code has expired.');
      case 'exhausted': return uiText('voucher.error.exhausted', 'This code has already been fully claimed.');
      case 'revoked': return uiText('voucher.error.invalid', 'That code is not valid.');
      case 'scheduled': return uiText('voucher.error.notStarted', 'This code is not active yet.');
      default: return null;
    }
  }

  /* -------------------------------------------------------- the object */

  function issueStamp() {
    try {
      const now = new Date();
      const month = now.toLocaleString(i18n?.getLanguage?.() || 'en', { month: 'long' }).toUpperCase();
      return `${month} / ${now.getFullYear()}`;
    } catch (_) {
      return '';
    }
  }

  function stamp(parent, className, label, value) {
    const block = document.createElement('div');
    block.className = `invite-ticket-stamp ${className}`;
    const top = document.createElement('span');
    top.className = 'invite-ticket-stamp-label';
    top.textContent = label;
    block.appendChild(top);
    if (value) {
      const bottom = document.createElement('span');
      bottom.className = 'invite-ticket-stamp-value';
      bottom.textContent = value;
      block.appendChild(bottom);
    }
    parent.appendChild(block);
    return block;
  }

  /** The same physical ticket the invitation prints, stamped as a voucher. */
  function buildVoucherTicket({ code, admitValue }) {
    const ticket = document.createElement('div');
    ticket.className = 'invite-ticket invite-ticket--voucher';
    ticket.dataset.voucherTicket = 'true';

    const art = document.createElement('img');
    art.className = 'invite-ticket-art';
    art.src = VOUCHER_TICKET_ART_SRC;
    art.alt = '';
    art.decoding = 'async';
    art.setAttribute('fetchpriority', 'high');
    ticket.appendChild(art);

    const face = document.createElement('div');
    face.className = 'invite-ticket-face';
    face.setAttribute('aria-hidden', 'true');

    const head = document.createElement('div');
    head.className = 'invite-ticket-face-row invite-ticket-face-row--head';
    stamp(head, 'invite-ticket-stamp--brand', 'MOONFALL', '');
    stamp(head, 'invite-ticket-stamp--date', issueStamp(), '');
    face.appendChild(head);

    const footer = document.createElement('div');
    footer.className = 'invite-ticket-face-foot';
    const issuer = document.createElement('div');
    issuer.className = 'invite-ticket-issuer invite-ticket-issuer--filled';
    issuer.textContent = uiText('voucher.ticketGift', 'A GIFT FOR YOU');
    const foot = document.createElement('div');
    foot.className = 'invite-ticket-face-row invite-ticket-face-row--foot';
    stamp(foot, 'invite-ticket-stamp--admit', uiText('voucher.ticketAdmit', 'VOUCHER'), admitValue);
    stamp(foot, 'invite-ticket-stamp--serial', uiText('invite.ticketSerial', 'NO.'), voucherCodeHint(code));
    footer.append(issuer, foot);
    face.appendChild(footer);
    ticket.appendChild(face);

    const glare = document.createElement('span');
    glare.className = 'invite-ticket-glare';
    glare.setAttribute('aria-hidden', 'true');
    ticket.appendChild(glare);
    keepTicketStubFitted(ticket);
    return { ticket, art };
  }

  function build({ code, preview }) {
    const lines = preview.grants.map(grantLine).filter(Boolean);
    const blocked = stateCopy(preview.state);

    const root = document.createElement('div');
    root.id = ROOT_ID;
    root.className = 'invite-ticket-view invite-ticket-view--voucher';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'voucher-ticket-title');

    const brand = document.createElement('img');
    brand.className = 'invite-ticket-brand';
    brand.src = '/assets/images/logo.webp';
    brand.alt = '';
    brand.decoding = 'async';
    brand.setAttribute('aria-hidden', 'true');
    root.appendChild(brand);

    const stage = document.createElement('div');
    stage.className = 'invite-ticket-stage';
    const { ticket, art } = buildVoucherTicket({ code, admitValue: voucherAdmitStamp(preview.grants) });
    stage.appendChild(ticket);

    const copy = document.createElement('div');
    copy.className = 'invite-ticket-copy';

    const title = document.createElement('h1');
    title.id = 'voucher-ticket-title';
    title.className = 'invite-ticket-title';
    title.textContent = blocked
      ? uiText('voucher.ticketBlockedTitle', 'This code cannot be claimed')
      : uiText('voucher.ticketTitle', 'A gift for you');

    const body = document.createElement('p');
    body.className = 'invite-ticket-body';
    body.textContent = blocked
      || uiText('voucher.ticketBody', 'Someone sent you a Moonfall voucher. Claim it and it is yours.');

    const list = document.createElement('ul');
    list.className = 'voucher-ticket-grants';
    for (const line of lines) {
      const item = document.createElement('li');
      item.className = 'voucher-ticket-grant';
      item.textContent = line;
      list.appendChild(item);
    }

    const error = document.createElement('p');
    error.className = 'voucher-ticket-error';
    error.setAttribute('role', 'alert');

    const actions = document.createElement('div');
    actions.className = 'voucher-ticket-actions';
    const claim = document.createElement('button');
    claim.type = 'button';
    claim.id = 'voucher-ticket-claim';
    claim.className = 'invite-ticket-accept game-command-btn';
    const later = document.createElement('button');
    later.type = 'button';
    later.id = 'voucher-ticket-later';
    later.className = 'voucher-ticket-later';
    later.textContent = blocked
      ? uiText('voucher.rewardCta', 'Continue')
      : uiText('voucher.ticketLater', 'Not now');
    if (!blocked) actions.appendChild(claim);
    actions.appendChild(later);

    copy.append(title, body);
    if (lines.length && !blocked) copy.appendChild(list);
    copy.append(error, actions);
    root.append(stage, copy);
    return { root, ticket, art, claim, later, error, blocked: Boolean(blocked) };
  }

  /* --------------------------------------------------------- the screen */

  function signedIn() {
    try { return Firebase?.isSignedIn?.() === true; } catch (_) { return false; }
  }

  /* The ticket on screen, so the lobby hand-off and auth changes can reach
     it: a label frozen on the first read said "Sign in" to a player whose
     session Firebase restored a moment after the ticket appeared. */
  let active = null;

  function stopFollowingAuth(root) {
    if (!active || active.root !== root) return;
    const { unsubscribe } = active;
    active = null;
    if (typeof unsubscribe === 'function') {
      try { unsubscribe(); } catch (_) { /* already gone */ }
    }
  }

  function dismiss(root, { claimed = false } = {}) {
    settleArrival();
    if (!root || !root.isConnected) return;
    stopFollowingAuth(root);
    const ticket = root.querySelector('.invite-ticket');
    if (claimed && ticket) ticket.classList.add('invite-ticket--claimed');
    root.classList.add('invite-ticket-view--leaving');
    const remove = () => root.remove();
    root.addEventListener('animationend', remove, { once: true });
    setTimeout(remove, 600);
  }

  /* THE TICKET ARRIVES WHOLE, AND THE APP WAITS FOR IT.
     Same contract as the invitation arrival (invite-ticket.js), through the
     same shared helpers: the artwork is warmed the moment a code is known,
     the boot veil stays up until the finished ticket is on screen (or a hard
     cap), and the surface reveals only once its own <img> has decoded. The
     voucher art is 372KB and used to start downloading when the ticket
     mounted, under a face already printed. */
  const VEIL_OWNER = 'voucher';
  const ARRIVAL_HOLD_CAP_MS = 10000;
  let artPreload = null;

  function preloadTicketArt() {
    if (!artPreload) artPreload = warmInviteTicketArt(VOUCHER_TICKET_ART_SRC);
    return artPreload;
  }

  function settleArrival() {
    releaseBootVeilForTicket(VEIL_OWNER);
  }

  function holdArrival() {
    try {
      if (!pendingCode()) return;
      holdBootVeilForTicket(VEIL_OWNER, { capMs: ARRIVAL_HOLD_CAP_MS });
      preloadTicketArt();
    } catch (_) { /* the registry's own cap still releases it */ }
  }

  function whenArtReady(art) {
    return whenInviteTicketWhole(art, { capMs: PRESENT_CAP_MS, extra: [artPreload] });
  }

  function present({ code, preview }) {
    if (document.getElementById(ROOT_ID) || !document.body) return false;
    const { root, ticket, art, claim, later, error, blocked } = build({ code, preview });
    root.classList.add('invite-ticket-view--holding');
    document.body.appendChild(root);
    storePreview(code, preview);

    const releaseTransform = () => ticket.classList.add('invite-ticket--settled');
    ticket.addEventListener('animationend', releaseTransform, { once: true });
    setTimeout(releaseTransform, 1100);
    try { CardTilt?.registerPreview?.(ticket); } catch (_) { /* tilt is decoration */ }

    const reveal = () => {
      if (!root.isConnected) { settleArrival(); return; }
      root.classList.remove('invite-ticket-view--holding');
      settleArrival();
      try { SoundEffects?.play?.('card_reveal'); } catch (_) { /* muted */ }
      const focusTarget = blocked ? later : claim;
      try { focusTarget.focus({ preventScroll: true }); } catch (_) { focusTarget.focus(); }
    };
    whenArtReady(art).then(reveal, reveal);

    const relabel = () => {
      claim.textContent = signedIn()
        ? uiText('voucher.ticketClaim', 'Claim your gift')
        : uiText('voucher.ticketSignIn', 'Sign in to claim');
    };
    relabel();
    let unsubscribe = null;
    try {
      unsubscribe = Firebase?.subscribe?.(() => {
        if (root.isConnected) relabel();
        else stopFollowingAuth(root);
      });
    } catch (_) { unsubscribe = null; }
    active = { root, relabel, unsubscribe };

    let claiming = false;
    claim.addEventListener('click', async () => {
      if (claiming) return;
      if (!signedIn()) {
        /* The code stays pending in this tab; the ticket returns in the
           lobby once the account exists. Same hand-off as the invitation. */
        try { MoonfallGuest?.endGuestTutorial?.('skip'); } catch (_) { /* never trap the visitor */ }
        dismiss(root);
        return;
      }
      claiming = true;
      claim.disabled = true;
      claim.classList.add('btn-pending');
      claim.setAttribute('aria-busy', 'true');
      error.textContent = '';
      const outcome = await VoucherRedeem.redeemCode(code, { source: 'link' });
      if (outcome.ok) {
        clearStoredPreview();
        dismiss(root, { claimed: true });
        return;
      }
      claiming = false;
      claim.disabled = false;
      claim.classList.remove('btn-pending');
      claim.setAttribute('aria-busy', 'false');
      error.textContent = outcome.message;
      if (outcome.final) clearStoredPreview();
    });

    later.addEventListener('click', () => {
      /* Dismissed, not kept: a ticket that re-announces itself on every
         lobby visit stops reading as a gift. The link still works. */
      try { MoonfallVoucherLink?.clearPendingCode?.(code); } catch (_) { /* ignore */ }
      clearStoredPreview();
      dismiss(root);
    });
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') later.click();
    });
    return true;
  }

  /* A code with no local preview asks the server. A code the server does not
     know is dropped for good; a failed request keeps it for the next visit. */
  async function presentFromServer(code) {
    const fetched = await fetchPreview(code);
    if (fetched?.exists === true) return present({ code, preview: fetched });
    if (fetched?.exists === false) {
      try { MoonfallVoucherLink?.clearPendingCode?.(code); } catch (_) { /* ignore */ }
    }
    return false;
  }

  /** On arrival: a link the server confirmed shows at once; a stored code asks first. */
  async function showOnArrival() {
    const code = pendingCode();
    if (!code) { settleArrival(); return false; }
    const known = seededPreview() || storedPreview(code);
    let shown = false;
    try {
      // Locale-stamped links must paint their dictionary on the first frame.
      // DOMContentLoaded can precede the shared i18n download completing.
      await i18n?.init?.();
      shown = known ? present({ code, preview: known }) : await presentFromServer(code);
    } finally {
      /* No ticket is coming (unknown code, a failed preview, a duplicate
         mount): let the app get on with booting. A shown ticket releases the
         veil itself once it is on screen. */
      if (!shown) settleArrival();
    }
    return shown;
  }

  /** In the lobby, queued behind the arrival ceremony: the ticket comes back to be claimed. */
  async function showPendingInLobby() {
    const code = pendingCode();
    if (!code || !signedIn()) return false;
    if (document.getElementById(ROOT_ID)) {
      try { active?.relabel?.(); } catch (_) { /* label only */ }
      return true;
    }
    const known = storedPreview(code) || seededPreview();
    return known ? present({ code, preview: known }) : presentFromServer(code);
  }

  const api = { showOnArrival, showPendingInLobby, present, __dismiss: dismiss };

  if (typeof document !== 'undefined') {
    holdArrival();
    const arrive = () => { void showOnArrival(); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arrive, { once: true });
    else arrive();
  }
  return api;
}());

export { VoucherTicket };
